/**
 * WORLD STATE — Structure-of-Arrays pools.
 *
 * This is the "lightweight ECS": each entity kind is a fixed-capacity pool of
 * typed arrays indexed by entity slot. No objects are allocated per entity in
 * the hot path; free slots are tracked with a free-list. Every array here is
 * plain data, so the whole world can be snapshotted by copying buffers
 * (see snapshot.ts) — the basis for save/replay and future netcode.
 *
 * Slot 0 of each pool is never used (0 == "no entity") to make refs cheap.
 */

export const MAX_PROJECTILES = 4096;
export const MAX_ENEMIES = 1024;
export const MAX_PICKUPS = 2048;
export const MAX_HAZARDS = 256;
export const MAX_PLAYERS = 4;
export const MAX_PETS = MAX_PLAYERS + 1;
/** Max concurrent emitters a single pattern may have (per-enemy emitter state is sized by this). */
export const MAX_EMITTERS = 8;

export const SIDE_PLAYER = 0;
export const SIDE_ENEMY = 1;

/** Element status bits carried on projectiles (from player stats at fire time). */
export const EL_FIRE = 1;
export const EL_FROST = 2;
export const EL_LIGHTNING = 4;
export const EL_TEMPORAL = 8;

/**
 * WORLD WRAP: the arena is a torus of size `World.size` (square). Positions
 * live in [-half, half). Every delta between two positions must go through
 * `wrapDelta` so the shortest path across the seam is used.
 */
export function wrapCoord(v: number, half: number): number {
  const size = half * 2;
  if (v >= half) v -= size;
  else if (v < -half) v += size;
  // second pass covers large jumps (dash across the seam)
  if (v >= half) v -= size;
  else if (v < -half) v += size;
  return v;
}

export function wrapDelta(d: number, half: number): number {
  const size = half * 2;
  if (d > half) d -= size;
  else if (d < -half) d += size;
  return d;
}

export class Pool {
  readonly alive: Uint8Array;
  readonly free: Int32Array;
  freeCount = 0;
  count = 0;
  /** Highest used slot + 1, so iteration can skip the tail. */
  high = 1;

  constructor(readonly cap: number) {
    this.alive = new Uint8Array(cap);
    this.free = new Int32Array(cap);
    this.reset();
  }

  reset(): void {
    this.alive.fill(0);
    this.freeCount = 0;
    for (let i = this.cap - 1; i >= 1; i--) this.free[this.freeCount++] = i;
    this.count = 0;
    this.high = 1;
  }

  /** Returns slot or 0 if full. */
  alloc(): number {
    if (this.freeCount === 0) return 0;
    const i = this.free[--this.freeCount];
    this.alive[i] = 1;
    this.count++;
    if (i + 1 > this.high) this.high = i + 1;
    return i;
  }

  release(i: number): void {
    if (!this.alive[i]) return;
    this.alive[i] = 0;
    this.free[this.freeCount++] = i;
    this.count--;
  }

  /** Recompute `high` occasionally so iteration stays tight after mass deaths. */
  compactHigh(): void {
    let h = this.high;
    while (h > 1 && !this.alive[h - 1]) h--;
    this.high = h;
  }
}

export class Projectiles {
  readonly pool = new Pool(MAX_PROJECTILES);
  readonly x = new Float32Array(MAX_PROJECTILES);
  readonly y = new Float32Array(MAX_PROJECTILES);
  readonly px = new Float32Array(MAX_PROJECTILES); // previous tick (render interpolation)
  readonly py = new Float32Array(MAX_PROJECTILES);
  readonly vx = new Float32Array(MAX_PROJECTILES);
  readonly vy = new Float32Array(MAX_PROJECTILES);
  readonly accel = new Float32Array(MAX_PROJECTILES);
  readonly minSpeed = new Float32Array(MAX_PROJECTILES);
  readonly maxSpeed = new Float32Array(MAX_PROJECTILES);
  readonly life = new Float32Array(MAX_PROJECTILES);
  readonly age = new Float32Array(MAX_PROJECTILES);
  readonly radius = new Float32Array(MAX_PROJECTILES);
  readonly damage = new Float32Array(MAX_PROJECTILES);
  readonly side = new Uint8Array(MAX_PROJECTILES);
  readonly owner = new Uint8Array(MAX_PROJECTILES); // player index for side 0
  readonly pierce = new Int16Array(MAX_PROJECTILES);
  readonly bounce = new Int16Array(MAX_PROJECTILES);
  readonly homing = new Float32Array(MAX_PROJECTILES);
  readonly homingDelay = new Float32Array(MAX_PROJECTILES);
  readonly curve = new Float32Array(MAX_PROJECTILES);
  readonly waveAmp = new Float32Array(MAX_PROJECTILES);
  readonly waveFreq = new Float32Array(MAX_PROJECTILES);
  readonly visual = new Uint8Array(MAX_PROJECTILES);
  readonly color = new Uint32Array(MAX_PROJECTILES);
  readonly elements = new Uint8Array(MAX_PROJECTILES);
  readonly critChance = new Float32Array(MAX_PROJECTILES);
  readonly critMult = new Float32Array(MAX_PROJECTILES);
  readonly lastHit = new Int32Array(MAX_PROJECTILES); // enemy slot last damaged (pierce dedupe)
  readonly onExpire = new Int16Array(MAX_PROJECTILES); // pattern index or -1
  readonly linger = new Int16Array(MAX_PROJECTILES); // linger def index into world.lingerDefs or -1
  readonly heading = new Float32Array(MAX_PROJECTILES); // cached atan2 for curve/wave (radians)
  readonly stillBonus = new Float32Array(MAX_PROJECTILES); // damage multiplier snapshot (player bullets)
}

export class Enemies {
  readonly pool = new Pool(MAX_ENEMIES);
  readonly x = new Float32Array(MAX_ENEMIES);
  readonly y = new Float32Array(MAX_ENEMIES);
  readonly px = new Float32Array(MAX_ENEMIES);
  readonly py = new Float32Array(MAX_ENEMIES);
  readonly vx = new Float32Array(MAX_ENEMIES);
  readonly vy = new Float32Array(MAX_ENEMIES);
  readonly hp = new Float32Array(MAX_ENEMIES);
  readonly maxHp = new Float32Array(MAX_ENEMIES);
  readonly radius = new Float32Array(MAX_ENEMIES);
  readonly speed = new Float32Array(MAX_ENEMIES);
  readonly def = new Int16Array(MAX_ENEMIES); // index into registry.enemyList; -1 for boss (see bossDef)
  readonly elite = new Uint8Array(MAX_ENEMIES);
  readonly isBoss = new Uint8Array(MAX_ENEMIES);
  /** Authored wave member (including its spawned descendants); used by wave-objective completion. */
  readonly objectiveWave = new Uint8Array(MAX_ENEMIES);
  readonly gen = new Uint16Array(MAX_ENEMIES); // generation counter for stale-ref detection
  /** Behavior state machine. */
  readonly state = new Uint8Array(MAX_ENEMIES);
  readonly timer = new Float32Array(MAX_ENEMIES);
  readonly facing = new Float32Array(MAX_ENEMIES);
  readonly age = new Float32Array(MAX_ENEMIES);
  /** Status effects. */
  readonly slow = new Float32Array(MAX_ENEMIES); // 0..1 fraction
  readonly slowT = new Float32Array(MAX_ENEMIES);
  readonly burnDps = new Float32Array(MAX_ENEMIES);
  readonly burnT = new Float32Array(MAX_ENEMIES);
  readonly hitFlash = new Float32Array(MAX_ENEMIES);
  readonly contactCd = new Float32Array(MAX_ENEMIES);
  /** Pattern runner. */
  readonly patternIdx = new Int16Array(MAX_ENEMIES); // registry pattern index, -1 = none active
  readonly patternT = new Float32Array(MAX_ENEMIES);
  readonly patternSlot = new Uint8Array(MAX_ENEMIES); // which entry of def.patterns is next
  readonly patternCd = new Float32Array(MAX_ENEMIES);
  readonly emitNext = new Float32Array(MAX_ENEMIES * MAX_EMITTERS); // next burst time per emitter
  readonly emitBursts = new Uint16Array(MAX_ENEMIES * MAX_EMITTERS); // bursts fired
  /** Boss-only. */
  readonly phase = new Uint8Array(MAX_ENEMIES);
  readonly phaseT = new Float32Array(MAX_ENEMIES);
  /** One-shot boss phase entry pattern has fired. Kept separate from behavior state. */
  readonly phaseEntryFired = new Uint8Array(MAX_ENEMIES);
  readonly summonT = new Float32Array(MAX_ENEMIES);
  /** Boss special-mechanic timers (interval countdown / active-effect countdown) and trail drop timer. */
  readonly specialT = new Float32Array(MAX_ENEMIES);
  readonly specialActive = new Float32Array(MAX_ENEMIES);
  readonly trailT = new Float32Array(MAX_ENEMIES);
  /** Leap: target landing point. */
  readonly leapX = new Float32Array(MAX_ENEMIES);
  readonly leapY = new Float32Array(MAX_ENEMIES);
  /** Slide: carried momentum (units/s), decayed by the behavior's friction. */
  readonly momX = new Float32Array(MAX_ENEMIES);
  readonly momY = new Float32Array(MAX_ENEMIES);
  /** Bitmask of shield-add slots is too big; instead count living shield adds per boss here. */
  readonly shieldAdds = new Int16Array(MAX_ENEMIES);
  /** For shield adds: slot of the boss they protect (0 = none). */
  readonly protects = new Int16Array(MAX_ENEMIES);
  readonly invuln = new Float32Array(MAX_ENEMIES);
  /** Dive behavior altitude (render hint + collision gate). */
  readonly z = new Float32Array(MAX_ENEMIES);
  /** Elite affixes: bitmask of AFFIX_BITS (src/content/affixes.ts), 0 = none. Bosses never set this. */
  readonly affixes = new Uint8Array(MAX_ENEMIES);
  /** 'shielded' affix: remaining shield HP (absorbs damage before hp). 0 = no/broken shield. */
  readonly shield = new Float32Array(MAX_ENEMIES);
}

export class Pickups {
  readonly pool = new Pool(MAX_PICKUPS);
  readonly x = new Float32Array(MAX_PICKUPS);
  readonly y = new Float32Array(MAX_PICKUPS);
  readonly kind = new Uint8Array(MAX_PICKUPS); // 0 xp, 1 heal, 2 shard
  /** XP/shard quantity; for hearts, fraction of the collector's max HP. */
  readonly amount = new Float32Array(MAX_PICKUPS);
  readonly life = new Float32Array(MAX_PICKUPS);
  readonly magnet = new Int8Array(MAX_PICKUPS); // player index being pulled to, -1 none
}

export class Hazards {
  readonly pool = new Pool(MAX_HAZARDS);
  readonly x = new Float32Array(MAX_HAZARDS);
  readonly y = new Float32Array(MAX_HAZARDS);
  readonly radius = new Float32Array(MAX_HAZARDS);
  readonly life = new Float32Array(MAX_HAZARDS);
  readonly maxLife = new Float32Array(MAX_HAZARDS);
  readonly dps = new Float32Array(MAX_HAZARDS);
  readonly color = new Uint32Array(MAX_HAZARDS);
  readonly side = new Uint8Array(MAX_HAZARDS);
  /** Telegraph: while > 0 the zone is drawn but deals no damage (boss `quake`, trap zones). */
  readonly armT = new Float32Array(MAX_HAZARDS);
}

/** One active companion per player. Small fixed SoA keeps pet state snapshot-friendly. */
export class Pets {
  readonly pool = new Pool(MAX_PETS);
  readonly x = new Float32Array(MAX_PETS);
  readonly y = new Float32Array(MAX_PETS);
  readonly px = new Float32Array(MAX_PETS);
  readonly py = new Float32Array(MAX_PETS);
  readonly owner = new Uint8Array(MAX_PETS);
  readonly def = new Uint8Array(MAX_PETS);
  readonly level = new Uint8Array(MAX_PETS);
  readonly fireT = new Float32Array(MAX_PETS);
  readonly facing = new Float32Array(MAX_PETS);
}

/** Per-player mutable state. Few instances → plain object is fine. */
export interface PlayerState {
  index: number;
  alive: boolean;
  x: number;
  y: number;
  px: number;
  py: number;
  vx: number;
  vy: number;
  facing: number;
  hp: number;
  /** Seconds since the player last had movement input (Infinity when never moved). */
  stillTime: number;
  moving: boolean;
  dashT: number; // remaining dash duration
  dashDx: number;
  dashDy: number;
  /** Last deliberate movement direction, kept separate from weapon-facing aim. */
  moveDx: number;
  moveDy: number;
  /** Remaining time for a recently pressed dash to execute when a charge becomes available. */
  dashBufferT: number;
  dashCdT: number;
  dashChargesLeft: number;
  iframes: number;
  fireCd: number;
  hitFlash: number;
  targetEnemy: number; // slot or 0
  shotIndex: number;
  temporalPulseT: number;
  /** Double Tap: echo shots still owed from the last shot, and the countdown to the next one. */
  echoLeft: number;
  echoT: number;
  /** Character-keystone state; generic fields keep snapshots compact and extensible. */
  keystoneShield: boolean;
  keystoneCooldown: number;
  keystoneCounter: number;
  keystoneWindow: number;
  deadeyeCharge: number;
  deadeyeArmed: boolean;
  regenAcc: number;
  /** Per-player run stats. */
  kills: number;
  damageDealt: number;
  damageTaken: number;
  /** Active companion entity slots owned by this player. */
  pets: number[];
}

export function newPlayerState(index: number): PlayerState {
  return {
    index,
    alive: true,
    x: 0,
    y: 0,
    px: 0,
    py: 0,
    vx: 0,
    vy: 0,
    facing: 0,
    hp: 100,
    stillTime: 0,
    moving: false,
    dashT: 0,
    dashDx: 0,
    dashDy: 0,
    moveDx: 1,
    moveDy: 0,
    dashBufferT: 0,
    dashCdT: 0,
    dashChargesLeft: 1,
    iframes: 0,
    fireCd: 0,
    hitFlash: 0,
    targetEnemy: 0,
    shotIndex: 0,
    temporalPulseT: 0,
    echoLeft: 0,
    echoT: 0,
    keystoneShield: false,
    keystoneCooldown: 0,
    keystoneCounter: 0,
    keystoneWindow: 0,
    deadeyeCharge: 0,
    deadeyeArmed: false,
    regenAcc: 0,
    kills: 0,
    damageDealt: 0,
    damageTaken: 0,
    pets: [],
  };
}

/**
 * Uniform spatial hash over enemies, rebuilt each tick in O(n) with no
 * allocations (counting sort into CSR layout). Queries iterate the cells that
 * overlap a circle.
 */
export class SpatialHash {
  cellSize: number;
  cols: number;
  rows: number;
  half: number;
  cellStart: Int32Array;
  cellCount: Int32Array;
  readonly items: Int32Array;
  private cellOf: Int32Array;

  constructor(worldHalfExtent: number, cellSize: number, capacity: number) {
    this.cellSize = cellSize;
    this.half = worldHalfExtent;
    this.cols = Math.max(1, Math.round((worldHalfExtent * 2) / cellSize));
    this.rows = this.cols;
    this.cellStart = new Int32Array(this.cols * this.rows + 1);
    this.cellCount = new Int32Array(this.cols * this.rows);
    this.items = new Int32Array(capacity);
    this.cellOf = new Int32Array(capacity);
  }

  /** Re-size the grid for a new arena (allocates; call at stage load only). */
  resize(worldHalfExtent: number): void {
    if (worldHalfExtent === this.half) return;
    this.half = worldHalfExtent;
    this.cols = Math.max(1, Math.round((worldHalfExtent * 2) / this.cellSize));
    this.rows = this.cols;
    this.cellStart = new Int32Array(this.cols * this.rows + 1);
    this.cellCount = new Int32Array(this.cols * this.rows);
  }

  cellIndex(x: number, y: number): number {
    let cx = ((x + this.half) / this.cellSize) | 0;
    let cy = ((y + this.half) / this.cellSize) | 0;
    cx = ((cx % this.cols) + this.cols) % this.cols;
    cy = ((cy % this.rows) + this.rows) % this.rows;
    return cy * this.cols + cx;
  }

  /** Wrapped cell coordinate (callers iterating qx0..qx1 must pass through this). */
  wrapCol(c: number): number {
    return ((c % this.cols) + this.cols) % this.cols;
  }
  wrapRow(c: number): number {
    return ((c % this.rows) + this.rows) % this.rows;
  }

  build(e: Enemies): void {
    const n = this.cols * this.rows;
    this.cellCount.fill(0, 0, n);
    const high = e.pool.high;
    for (let i = 1; i < high; i++) {
      if (!e.pool.alive[i]) continue;
      const c = this.cellIndex(e.x[i], e.y[i]);
      this.cellOf[i] = c;
      this.cellCount[c]++;
    }
    let acc = 0;
    for (let c = 0; c < n; c++) {
      this.cellStart[c] = acc;
      acc += this.cellCount[c];
    }
    this.cellStart[n] = acc;
    this.cellCount.fill(0, 0, n);
    for (let i = 1; i < high; i++) {
      if (!e.pool.alive[i]) continue;
      const c = this.cellOf[i];
      this.items[this.cellStart[c] + this.cellCount[c]++] = i;
    }
  }

  /** Cell range of the last `prepare` call (closure-free iteration for hot paths). */
  qx0 = 0;
  qx1 = 0;
  qy0 = 0;
  qy1 = 0;

  /**
   * Closure-free query: sets qx0..qy1; caller loops
   *   for cy in qy0..qy1, cx in qx0..qx1: c = cy*cols+cx; for k in cellStart[c]..cellStart[c+1]: slot = items[k]
   */
  prepare(x: number, y: number, r: number): void {
    const cs = this.cellSize;
    const x0 = Math.floor((x - r + this.half) / cs);
    let x1 = Math.floor((x + r + this.half) / cs);
    const y0 = Math.floor((y - r + this.half) / cs);
    let y1 = Math.floor((y + r + this.half) / cs);
    if (x1 - x0 >= this.cols) x1 = x0 + this.cols - 1;
    if (y1 - y0 >= this.rows) y1 = y0 + this.rows - 1;
    this.qx0 = x0;
    this.qx1 = x1;
    this.qy0 = y0;
    this.qy1 = y1;
  }

  /**
   * Visit each enemy slot whose cell overlaps the circle. `fn` returns true to
   * stop early. Convenience API (allocates a closure) — use `prepare` in hot paths.
   */
  query(x: number, y: number, r: number, fn: (slot: number) => boolean | void): void {
    this.prepare(x, y, r);
    for (let cy = this.qy0; cy <= this.qy1; cy++) {
      for (let cx = this.qx0; cx <= this.qx1; cx++) {
        const c = this.wrapRow(cy) * this.cols + this.wrapCol(cx);
        const s = this.cellStart[c];
        const e = this.cellStart[c + 1];
        for (let k = s; k < e; k++) {
          if (fn(this.items[k])) return;
        }
      }
    }
  }
}

export interface LingerDef {
  radius: number;
  duration: number;
  dps: number;
  color: number;
}

export class World {
  readonly projectiles = new Projectiles();
  readonly enemies = new Enemies();
  readonly pickups = new Pickups();
  readonly hazards = new Hazards();
  readonly pets = new Pets();
  readonly players: PlayerState[] = [];
  readonly grid: SpatialHash;
  /** Interned linger defs referenced by index from projectiles (data, not per-tick allocs). */
  readonly lingerDefs: LingerDef[] = [];
  private lingerKeys = new Map<string, number>();
  /** Half-size of the square toroidal arena (world is [-half, half) on both axes). */
  arenaRadius = 60;
  get half(): number {
    return this.arenaRadius;
  }
  /** Sim time (seconds) since run start. */
  time = 0;
  tick = 0;

  constructor(playerCount: number) {
    this.grid = new SpatialHash(this.arenaRadius, 4, MAX_ENEMIES);
    for (let i = 0; i < playerCount; i++) this.players.push(newPlayerState(i));
  }

  /** Forget every interned linger def (snapshot restore rebuilds the table in index order). */
  resetLingers(): void {
    this.lingerDefs.length = 0;
    this.lingerKeys.clear();
  }

  internLinger(l: LingerDef): number {
    const key = `${l.radius}|${l.duration}|${l.dps}|${l.color}`;
    let idx = this.lingerKeys.get(key);
    if (idx === undefined) {
      idx = this.lingerDefs.length;
      this.lingerDefs.push({ ...l });
      this.lingerKeys.set(key, idx);
    }
    return idx;
  }

  clearEntities(): void {
    this.projectiles.pool.reset();
    this.enemies.pool.reset();
    this.pickups.pool.reset();
    this.hazards.pool.reset();
    this.pets.pool.reset();
    for (const player of this.players) player.pets.length = 0;
  }
}
